import React, { useEffect, useState, ChangeEvent } from 'react';
import { observer } from 'mobx-react-lite';

import { useAnalytics } from '../../../utils/analytics';
import { useMst } from '../../../models/rootModel';
import { capitalizeFirstLetter } from '../../../utils/utilsFunctions';

import { BaseStep, TitleAndDescriptionProps, StepProps, StepWizardProps } from '../../common/BaseStep';
import { InputField } from '../../common/InputField/InputField';
import { TermsOfServicePopUp } from '../../common/TermsOfServicePopUp';

import { SignUpFields, SignUpValues } from './SignUp.types';
import { TermsAndConditions } from './TermsAndConditions';

import { InputRoot, Label, ErrorInputText } from '../../styledComponent/SignUpComponents.style';
import { StepWrapperBox } from '../../styledComponent/Boxes.style';
import { SignUpBottomTextInput } from '../../styledComponent/Text.style';

export interface SignUpEmailValues extends Omit<SignUpValues, SignUpFields.Phone> {}

export interface SignUpEmailStepProps extends TitleAndDescriptionProps, StepProps, StepWizardProps {
  inputFields: {
    type: 'name' | 'email';
    label: string;
    placeHolder: string;
    explanation?: string;
  }[];
}

export const SignUpEmailStep = observer((props: SignUpEmailStepProps) => {
  const [isValidInputs, setIsValidInputs] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isTermsPopUpOpen, setIsTermsPopUpOpen] = useState(false);
  const [signUpValues, setSignUpValues] = useState<SignUpEmailValues>({
    [SignUpFields.Name]: '',
    [SignUpFields.Email]: '',
  });

  const [signUpInputError, setSignUpInputError] = useState<SignUpEmailValues>({
    [SignUpFields.Name]: '',
    [SignUpFields.Email]: '',
  });

  const { UserDiscoveryResponse, Step } = useMst();
  const { inputFields, nextStep, isActive, stepType } = props;

  const analytics = useAnalytics();

  useEffect(() => {
    if (isActive) {
      analytics.track('view signup step', { referringUrl: document.referrer })
      window.scrollTo(0, 0)
    }
  }, [isActive, analytics]);

  useEffect(() => {
    if (isActive && isValidInputs && isButtonDisabled) {
      setIsButtonDisabled(false);
    }
    if (isActive && !isButtonDisabled && !isValidInputs) {
      setIsButtonDisabled(true);
    }
  }, [isActive, isValidInputs, isButtonDisabled]);

  useEffect(() => {
    const checkValidInputs = signUpValues[SignUpFields.Name].length && signUpValues[SignUpFields.Email].length;
    checkValidInputs ? setIsValidInputs(true) : setIsValidInputs(false);
  }, [signUpValues]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    // Doesn't allow to enter number or other illegal chars, allows space, letters and ' -
    if (name === 'name' && /([\d!@#$%^&*()_+={}"|\\/[\]`,.?])+/.test(value)) {
      return;
    }
    setSignUpInputError((prevValues) => ({
      ...prevValues,
      [name]: '',
    }));

    setSignUpValues((prevState) => {
      return { ...prevState, [name]: value };
    });
  };

  const onSubmit = () => {
    setIsButtonDisabled(true);
    UserDiscoveryResponse.setUserNameAndEmail(signUpValues);

    analytics.track(`click signUpEmail complete ${props.type}`, {
      stepId: props.id,
      stepType: props.type,
      stepIndex: props.stepIndex,
      stepTitle: props.title ?? '',
      ...UserDiscoveryResponse.utms,
      origin: Step.origin,
      userID: UserDiscoveryResponse.accountId,
    });

    nextStep();
  };

  const validatedSignUpInformation = () => {
    let isThereError = false;
    analytics.track('signup confirm click', { stepType })

    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(signUpValues[SignUpFields.Email])) {
      isThereError = true;
      setSignUpInputError((prevValues) => ({
        ...prevValues,
        email: 'Please enter a valid email. E.g. name@domain.com',
      }));
    }

    if (signUpValues[SignUpFields.Email] === 'name@domain.com') {
      isThereError = true;
      setSignUpInputError((prevValues) => ({
        ...prevValues,
        email: 'Use your real email :)',
      }));
    }

    if (isThereError) {
      setIsValidInputs(false);
      return;
    }

    setIsValidInputs(true);
    onSubmit();
  };

  const idStart = 'signUpEmailForm';

  return (
    <>
      {isActive ? (
        <BaseStep {...props} isButtonDisabled={isButtonDisabled} changeButtonFunction={validatedSignUpInformation}>
          <TermsOfServicePopUp open={isTermsPopUpOpen} handleClose={() => setIsTermsPopUpOpen(false)} />
          <StepWrapperBox>
            {inputFields.map((inputField, index) => (
              <InputRoot key={`${inputField.type}-${index}`}>
                <Label>{inputField.label}</Label>
                <InputField
                  id={`${idStart}${capitalizeFirstLetter(inputField.type)}`}
                  type="email"
                  error={signUpInputError[inputField.type]}
                  name={inputField.type}
                  handleChange={(event: ChangeEvent<HTMLInputElement>) => handleChange(event)}
                  value={signUpValues[inputField.type]}
                  placeholder={inputField.placeHolder}
                  bottomText={inputField.explanation ?? null}
                />

                {inputField.explanation && signUpInputError[inputField.type].length === 0 ? (
                  <SignUpBottomTextInput>{inputField.explanation}</SignUpBottomTextInput>
                ) : (
                  ''
                )}

                {signUpInputError[inputField.type].length > 0 ? (
                  <ErrorInputText>{signUpInputError[inputField.type]}</ErrorInputText>
                ) : (
                  ''
                )}
              </InputRoot>
            ))}

            <TermsAndConditions id={`${idStart}Terms`} setIsTermsPopUpOpen={setIsTermsPopUpOpen} />
          </StepWrapperBox>
        </BaseStep>
      ) : (
        ''
      )}
    </>
  );
});
